import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Worker, WorkerPayment } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function firstOfMonthStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function endOfMonthStr(): string {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const y = last.getFullYear();
  const m = String(last.getMonth() + 1).padStart(2, "0");
  const dd = String(last.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function PaymentsTab() {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [filterWorker, setFilterWorker] = useState<string | null>(null);
  const [from, setFrom] = useState<string>(firstOfMonthStr());
  const [to, setTo] = useState<string>(endOfMonthStr());
  const [payments, setPayments] = useState<WorkerPayment[]>([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    worker_id: null as string | null,
    paid_at: todayStr(),
    amount: "",
    covers_from: "",
    covers_to: "",
    note: "",
  });

  const workerById = useMemo(() => new Map(workers.map((w) => [w.id, w] as const)), [workers]);

  async function reload() {
    setLoading(true);
    try {
      const ws = await api.listWorkers();
      setWorkers(ws);
      const params: Parameters<typeof api.listWorkerPayments>[0] = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (filterWorker) params.worker_id = Number(filterWorker);
      const ps = await api.listWorkerPayments(params);
      setPayments(ps);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [filterWorker, from, to]);

  async function add() {
    if (!form.worker_id || !form.amount) {
      notifications.show({ color: "red", message: t("workers.payments.required") });
      return;
    }
    try {
      await api.createWorkerPayment({
        worker_id: Number(form.worker_id),
        paid_at: form.paid_at || todayStr(),
        amount: Number(form.amount),
        covers_from: form.covers_from || null,
        covers_to: form.covers_to || null,
        note: form.note.trim() || null,
      });
      setOpen(false);
      setForm({
        worker_id: null,
        paid_at: todayStr(),
        amount: "",
        covers_from: "",
        covers_to: "",
        note: "",
      });
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function remove(id: number) {
    if (!confirm(t("workers.payments.confirmDelete"))) return;
    try {
      await api.deleteWorkerPayment(id);
      await reload();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  const total = payments.reduce((acc, p) => acc + p.amount, 0);
  const workerOptions = workers.map((w) => ({ value: String(w.id), label: w.full_name }));

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="sm" wrap="wrap" align="end">
          <Select
            label={t("workers.payments.worker")}
            data={workerOptions}
            value={filterWorker}
            onChange={setFilterWorker}
            clearable
            searchable
            w={220}
          />
          <TextInput
            type="date"
            label={t("workers.payments.from")}
            value={from}
            onChange={(e) => setFrom(e.currentTarget.value)}
            w={160}
          />
          <TextInput
            type="date"
            label={t("workers.payments.to")}
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
            w={160}
          />
        </Group>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setOpen(true)}>
          {t("workers.payments.add")}
        </Button>
      </Group>

      <Paper withBorder p="sm">
        <Group gap="xs">
          <Text size="sm" c="dimmed">{t("workers.payments.totalInFilter")}</Text>
          <Text fw={600}>{total.toFixed(2)}</Text>
        </Group>
      </Paper>

      <ResponsiveTable
        mobile={{
          rows: payments,
          rowKey: (p) => p.id,
          title: (p) => workerById.get(p.worker_id)?.full_name ?? `#${p.worker_id}`,
          subtitle: (p) => `${p.paid_at} · ${p.amount.toFixed(2)}`,
          fields: [
            { label: t("workers.payments.covers"), render: (p) => (p.covers_from || p.covers_to ? `${p.covers_from ?? "?"} → ${p.covers_to ?? "?"}` : "—") },
            { label: t("workers.payments.note"), render: (p) => p.note ?? "—" },
          ],
          actions: (p) => (
            <ActionIcon size="lg" color="red" variant="subtle" onClick={() => void remove(p.id)} title={t("common.delete")}>
              <IconTrash size={16} />
            </ActionIcon>
          ),
          loading: loading && payments.length === 0,
          empty: t("workers.payments.empty"),
        }}
      >
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("workers.payments.paidAt")}</Table.Th>
            <Table.Th>{t("workers.payments.worker")}</Table.Th>
            <Table.Th>{t("workers.payments.amount")}</Table.Th>
            <Table.Th>{t("workers.payments.covers")}</Table.Th>
            <Table.Th>{t("workers.payments.note")}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {payments.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text c="dimmed" size="sm">{loading ? t("common.loading") : t("workers.payments.empty")}</Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            payments.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>{p.paid_at}</Table.Td>
                <Table.Td>{workerById.get(p.worker_id)?.full_name ?? `#${p.worker_id}`}</Table.Td>
                <Table.Td>{p.amount.toFixed(2)}</Table.Td>
                <Table.Td>{p.covers_from || p.covers_to ? `${p.covers_from ?? "?"} → ${p.covers_to ?? "?"}` : "—"}</Table.Td>
                <Table.Td>{p.note ?? "—"}</Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    <ActionIcon color="red" variant="subtle" onClick={() => void remove(p.id)} title={t("common.delete")}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
      </ResponsiveTable>

      <Modal opened={open} onClose={() => setOpen(false)} title={t("workers.payments.add")} centered>
        <Stack>
          <Select
            label={t("workers.payments.worker")}
            data={workerOptions}
            value={form.worker_id}
            onChange={(v) => setForm({ ...form, worker_id: v })}
            searchable
            required
          />
          <TextInput
            type="date"
            label={t("workers.payments.paidAt")}
            value={form.paid_at}
            onChange={(e) => setForm({ ...form, paid_at: e.currentTarget.value })}
            required
          />
          <NumberInput
            label={t("workers.payments.amount")}
            value={form.amount}
            onChange={(v) => setForm({ ...form, amount: typeof v === "number" ? String(v) : (v ?? "") })}
            min={0}
            decimalScale={2}
            allowNegative={false}
            required
          />
          <Group grow>
            <TextInput
              type="date"
              label={t("workers.payments.coversFrom")}
              value={form.covers_from}
              onChange={(e) => setForm({ ...form, covers_from: e.currentTarget.value })}
            />
            <TextInput
              type="date"
              label={t("workers.payments.coversTo")}
              value={form.covers_to}
              onChange={(e) => setForm({ ...form, covers_to: e.currentTarget.value })}
            />
          </Group>
          <TextInput
            label={t("workers.payments.note")}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.currentTarget.value })}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => void add()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
